import { ConnectProto } from "@racoon/api";
import type { TFn } from "@racoon/i18n";
import { fromTimestamp, getEnumName } from "@racoon/lib";
import { compareIds } from "~/shared/lib/compare-ids";
import { adaptJobToRow, type OrderRow } from "./adapters";
import { getPriorityScore } from "./orders.helpers";

const { JobStatus, PickupLocationPrioritySchema } = ConnectProto;

/** The service was carried out — the same statuses the backend counts as completed. */
export const isCompleted = (job: ConnectProto.Job) =>
  job.status === JobStatus.COMPLETED || job.status === JobStatus.FORCE_COMPLETED;

/**
 * An order: the service a customer asked for. Its jobs are the attempts at it — a
 * job the truck could not carry out (невывоз) is re-issued as a new job under the
 * same order number.
 */
type JobMapOrder = {
  /** Unique across the whole map. */
  key: string;
  /** Oldest attempt first. */
  jobs: ConnectProto.Job[];
  /** The attempt whose status is the order's, see `currentJobOf`. */
  current: ConnectProto.Job;
};

/** A pickup location served for one customer, with its orders in the order they arrived. */
export type JobMapLocation = {
  key: string;
  orders: JobMapOrder[];
};

const byIdAscending = (a: ConnectProto.Job, b: ConnectProto.Job) => compareIds(a.id, b.id);

/** When an attempt ends: its plan's end, else its window's; neither (a retry nobody planned) last. */
const rankedEnd = (job: ConnectProto.Job) =>
  fromTimestamp(job.planEndAt)?.getTime() ?? fromTimestamp(job.twEndAt)?.getTime() ?? Infinity;

/**
 * The attempt that speaks for an order, as the backend's `one_per_order` picks it: a
 * completed one over any other — the order was serviced, whatever happened to the
 * rest — then the latest end, then the newest id.
 */
export const currentJobOf = (jobs: ConnectProto.Job[]): ConnectProto.Job =>
  [...jobs].sort(
    (a, b) =>
      Number(isCompleted(b)) - Number(isCompleted(a)) ||
      // Two unplanned retries are both Infinity: NaN, so the ids decide.
      rankedEnd(b) - rankedEnd(a) ||
      compareIds(b.id, a.id),
  )[0];

/** One site can be served for several customers; each pairing is a group of its own. */
const locationKeyOf = (job: ConnectProto.Job) =>
  `pl:${job.pickupLocationId}:${job.serviceReceiverId}`;

/** A job without an order number is an order of its own. */
const orderKeyOf = (job: ConnectProto.Job) =>
  job.orderNumber ? `order:${job.orderNumber}` : `job:${job.id}`;

const isServiced = (location: JobMapLocation) =>
  location.orders.every((order) => isCompleted(order.current));

const priorityScoreOf = (location: JobMapLocation) =>
  getPriorityScore(
    getEnumName(PickupLocationPrioritySchema, location.orders[0].current.pickupLocation?.priority),
  );

/**
 * Files jobs under pickup location → order → attempt. A job joins its order wherever
 * it arrives, so an order whose jobs land on different pages of a paged list comes
 * back together. Locations with an order still open come first, then higher ПЛ
 * priority; otherwise locations and orders keep the order they arrived in.
 */
export const groupJobMap = (jobs: ConnectProto.Job[]): JobMapLocation[] => {
  const byLocation = new Map<string, Map<string, ConnectProto.Job[]>>();
  for (const job of jobs) {
    const locationKey = locationKeyOf(job);
    let byOrder = byLocation.get(locationKey);
    if (!byOrder) {
      byOrder = new Map();
      byLocation.set(locationKey, byOrder);
    }
    const orderKey = orderKeyOf(job);
    const attempts = byOrder.get(orderKey);
    if (attempts) attempts.push(job);
    else byOrder.set(orderKey, [job]);
  }

  const locations = [...byLocation].map(([key, byOrder]) => ({
    key,
    orders: [...byOrder].map(([orderKey, attempts]) => {
      const oldestFirst = [...attempts].sort(byIdAscending);
      return { key: `${key}/${orderKey}`, jobs: oldestFirst, current: currentJobOf(oldestFirst) };
    }),
  }));

  return locations.sort((a, b) => {
    const servicedA = isServiced(a);
    if (servicedA !== isServiced(b)) return servicedA ? 1 : -1;
    return priorityScoreOf(b) - priorityScoreOf(a);
  });
};

/** An order's jobs, newest first, each numbered by its attempt at the order: 1 for the first. */
export const numberAttempts = (jobRows: OrderRow[]): OrderRow[] =>
  [...jobRows]
    .sort((a, b) => byIdAscending(a.rawJob, b.rawJob))
    .map((row, index) => ({ ...row, _attempt: index + 1 }))
    .reverse();

/**
 * The map's rows for `useGroupedRows`: each location's header, then each of its
 * orders as a header nested in it — drawn with the table's columns, from its
 * current job — followed by one row carrying the order's jobs, which draws them
 * as a table of their own (see `OrderJobsTable`).
 */
export const jobMapRows = (locations: JobMapLocation[], t?: TFn): OrderRow[] =>
  locations.flatMap((location) => {
    const orders = location.orders.map((order) => ({
      order,
      jobRows: order.jobs.map((job) => adaptJobToRow(job, t)),
    }));
    // Every job in a location shares its site and customer, so any of them names it.
    const [named] = orders[0].jobRows;
    const header: OrderRow = {
      ...named,
      id: `_group_${location.key}`,
      _mapRow: "location",
      _isGroupHeader: true,
      _groupKey: location.key,
      _groupLocationName: named.objectName,
      _groupCompletedCount: location.orders.filter((order) => isCompleted(order.current)).length,
      _groupTotalCount: location.orders.length,
    };

    return [
      header,
      ...orders.flatMap(({ order, jobRows }) => [
        {
          ...jobRows[order.jobs.indexOf(order.current)],
          _mapRow: "order" as const,
          _isGroupHeader: true,
          _groupDepth: 1,
          _groupKey: order.key,
        },
        {
          ...jobRows[0],
          id: `_jobs_${order.key}`,
          _mapRow: "jobs" as const,
          _groupKey: order.key,
          _orderJobs: numberAttempts(jobRows),
        },
      ]),
    ];
  });

/** Distinct per row: an order's header, its jobs and the gap above it share its key. */
export const jobMapRowKey = (row: OrderRow): string => {
  if (row._mapRow === "spacer") return `spacer:${row._groupKey}`;
  if (row._mapRow === "jobs") return `jobs:${row._groupKey}`;
  return `group:${row._groupKey}`;
};

/** A pickup location's header, drawn as a custom row; an order's header uses the columns. */
export const isLocationHeader = (row: OrderRow): boolean => row._mapRow === "location";
